/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    Button,
    Card,
    DatePicker,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectElement,
    Table,
} from "@jhool-io/fe-components";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import {
    cn,
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import { IAppCustomDates } from "../../../../utils/types";
import ListState from "../../../../components/ListState/ListState";
import { useFetchAgingReport } from "../../hooks/queries/reports.queries";
import { DayInterval, IAgingReportResponse } from "../../types/reports.types";
import { useExportBillingSummaryReport } from "../../../billing/hooks/queries/billing.queries";
import useToast from "../../../../hooks/useToast";
import ChevronDownIcon from "../../../../components/Icons/ChevronDown";
import { useFetchClientList } from "../../../../hooks/queries/client";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { BillStatus } from "../../../billing/types/billing.types";
import { getBillStatusFilterLabel } from "../../../billing/helpers/billing.helpers";
import { useAppSelector } from "../../../../hooks/useRedux";
import CalendarIcon from "../../../../icons/Calendar";
import Loader from "../../../../shared-ui/Loader/Loader";

export default function CoInsuranceAgingReport() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isExporting, setIsExporting] = React.useState(false);
    const [clientSearchValue, setClientSearchValue] = React.useState("");
    const [providerSearchValue, setProviderSearchValue] = React.useState("");
    const [showClientOptions, setShowClientOptions] = React.useState(false);
    const [showProviderOptions, setShowProviderOptions] = React.useState(false);
    const [showDefaultClients, setShowDefaultClients] = React.useState(false);
    const [showDefaultProviders, setShowDefaultProviders] =
        React.useState(false);

    type Options = {
        label: React.ReactNode;
        value: string;
    };

    const providersFilter = searchParams.getAll("providers[]");
    const clientsFilter = searchParams.getAll("clients[]");
    const customDateFilter =
        (searchParams.get("custom_date") as IAppCustomDates) || undefined;
    const dayIntervalFilter =
        (searchParams.get("day_interval") as DayInterval) || undefined;

    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const pageFilter = Number(searchParams.get("page") || 1);
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const [sortConfig, setSortConfig] = React.useState<{
        key: string;
        direction: string;
    }>({
        key: "",
        direction: "ASC",
    });

    const getInvoiceStatus = () => {
        const invoiceStatusFilter = searchParams.getAll("invoice_status[]");

        if (invoiceStatusFilter.length > 0) {
            if (invoiceStatusFilter.includes("all")) {
                return [];
            }
            return invoiceStatusFilter;
        }

        return [];
    };

    const getIntervals = () => {
        const intervalsFilter = searchParams.getAll("day_intervals[]");

        if (intervalsFilter.length > 0) {
            if (intervalsFilter.includes("all")) {
                return [];
            }
            return intervalsFilter;
        }

        return [];
    };

    const clientSelectRef = React.useRef<SelectElement | null>(null);
    const providerSelectRef = React.useRef<SelectElement | null>(null);

    const sortableColumns = [
        "date_of_service",
        "client_name",
        "collected",
        "dob",
        "cpt_code",
    ];

    const handleConfigAttribute = () => {
        if (sortableColumns.includes(sortConfig.key)) {
            return sortConfig.key as string;
        }

        return "client_name";
    };

    const shouldFetchReport =
        clientsFilter.length > 0 ||
        providersFilter.length > 0 ||
        Boolean(customDateFilter) ||
        Boolean(dayIntervalFilter) ||
        Boolean(fromDateFilter) ||
        Boolean(toDateFilter) ||
        getInvoiceStatus().length > 0 ||
        getIntervals().length > 0;

    const { data, error, isLoading, isSuccess } = useFetchAgingReport(
        {
            view: "amount_outstanding",
            type: "coinsurance",
            show_report_data: true,
            client_names: clientsFilter,
            provider_names: providersFilter,
            custom_date: customDateFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            sort_attribute:
                searchParams.get("sort_attr") || handleConfigAttribute(),
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
            page: pageFilter,
            limit: limitFilter,
            aging_day_intervals: getIntervals(),
            invoice_status: getInvoiceStatus(),
        },
        shouldFetchReport
    );

    const { practice } = useAppSelector((state) => state.userPractice);

    const debouncedClientSearch = useDebounce(clientSearchValue, 500);

    const debouncedProviderSearch = useDebounce(providerSearchValue, 500);

    const toast = useToast();
    const navigate = useNavigate();

    const exportQuery = useExportBillingSummaryReport(
        {
            view: "amount_outstanding",
            type: "coinsurance",
            from_date: fromDateFilter,
            to_date: toDateFilter,
            search_strings: clientsFilter,
            provider_names: providersFilter,
            custom_date: customDateFilter,
            aging_day_intervals: getIntervals(),
            invoice_status: getInvoiceStatus(),
            sort_attribute:
                searchParams.get("sort_attr") || handleConfigAttribute(),
            sort_order: searchParams.get("sort_order") || sortConfig.direction,
        },
        isExporting
    );

    const { data: clients, isLoading: fetchingClients } = useFetchClientList(
        {
            search_string: debouncedClientSearch.toLowerCase(),
            provider_id: practice?.provider_id || "",
            sort_attribute: "client_name",
            sort_order: "ASC",
        },
        (showClientOptions && clientSearchValue.length > 0) ||
            clientsFilter.length > 0
    );

    const { data: providers } = useFetchProviders(
        {
            search_string: debouncedProviderSearch.toLowerCase(),
            sort_attribute: "provider_name",
            sort_order: "ASC",
        },
        (showProviderOptions && providerSearchValue.length > 0) ||
            providersFilter.length > 0
    );

    const ClientNamesForSelect = clients?.data.map((client) => ({
        label: `${client.first_name} ${client.last_name}`,
        value: `${client.first_name} ${client.last_name}`,
    }));

    // Get filtered options for clients list
    const filteredClients = ClientNamesForSelect?.filter((option) =>
        option.label.toLowerCase().includes(clientSearchValue.toLowerCase())
    );

    const ProviderNamesForSelect = providers?.data.map((provider) => ({
        label: provider.provider_name,
        value: provider.provider_name,
    }));

    // Get filtered options for providers list
    const filteredProviders = ProviderNamesForSelect?.filter((option) =>
        option.label.toLowerCase().includes(providerSearchValue.toLowerCase())
    );

    const DurationTypesForSelect: Options[] = [
        ...Object.values(IAppCustomDates).map((item) => ({
            value: item,
            label: (
                <>{makeStringFirstLetterCapital(removeEnumUnderscore(item))} </>
            ),
        })),
    ];

    const dayIntervalForSelect = [
        ...Object.values(DayInterval).map((item) => {
            return item;
        }),
    ];

    // onChange handler for page
    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    const handleClientsChange = (values: unknown) => {
        // Clear array values so we don't have duplicates
        // or values that have been removed from the array

        searchParams.delete("clients[]");
        if ((values as Options[]).length === 0) {
            searchParams.delete("clients[]");
        } else {
            (values as Options[]).forEach((option) => {
                searchParams.append("clients[]", option.value);
            });
        }

        setSearchParams(searchParams);
    };

    const handleProviderChange = (values: unknown) => {
        // Clear array values so we don't have duplicates
        // or values that have been removed from the array

        searchParams.delete("providers[]");
        if ((values as Options[]).length === 0) {
            searchParams.delete("providers[]");
        } else {
            (values as Options[]).forEach((option) => {
                searchParams.append("providers[]", option.value);
            });
        }

        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: unknown) => {
        // // Clear value if no value was selected
        if (!value) {
            searchParams.delete("custom_date");
        } else searchParams.set("custom_date", (value as Options).value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleDayIntervalChange = (interval: DayInterval) => {
        if (getIntervals().includes(interval)) {
            searchParams.delete("day_intervals[]", interval);
        } else {
            searchParams.append("day_intervals[]", interval);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleStatusChange = (status: string) => {
        if (getInvoiceStatus().includes(status)) {
            searchParams.delete("invoice_status[]", status);
        } else {
            searchParams.append("invoice_status[]", status);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));

        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));

        setSearchParams(searchParams);
    };

    const handleReset = () => {
        clientSelectRef.current?.clearValue();
        providerSelectRef.current?.clearValue();
        setSearchParams(() => "?report=billing_report&tab=coinsurance");
    };

    const handleExportClick = async () => {
        setIsExporting(true);
        await exportQuery.refetch();
        setIsExporting(false);
        if (exportQuery.error) {
            toast.toast({
                mode: "error",
                message: "Could not export report.",
            });
        }
    };

    const handleRowClick = (row: Row<IAgingReportResponse>) => {
        navigate(
            `/billing-and-claims/${row.original.client.client_id}/${row.original.note_id}`
        );
    };

    const columns: ColumnDef<IAgingReportResponse>[] = [
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) =>
                row.original.date_of_service
                    ? formatDate(row.original.date_of_service)
                    : "--",
        },
        {
            accessorKey: "client_name",
            header: "CLIENT",
            cell: ({ row }) =>
                displayNameInRightFormat({
                    firstName: row.original.client.first_name,
                    lastName: row.original.client.last_name,
                }),
        },
        {
            id: "dob",
            accessorKey: "date_of_birth",
            header: "DOB",
            cell: ({ row }) =>
                formatDate(row.original.client.date_of_birth, true),
        },
        {
            accessorKey: "insurance_provider_name",
            header: "INSURANCE",
            cell: ({ row }) => (
                <span>
                    {row.original.insurance_provider_name?.toUpperCase() ??
                        "--"}
                </span>
            ),
        },
        {
            id: "charge_amount",
            accessorKey: "charge_amount",
            header: "CLAIM CHARGE",
            cell: ({ row }) => showMoneyInAppFormat(row.original.charge_amount),
        },
        {
            accessorKey: "collected",
            header: "COLLECTED",
            cell: ({ row }) => showMoneyInAppFormat(row.original.collected),
        },
        {
            accessorKey: "amount_outstanding",
            header: "BALANCE DUE",
            cell: ({ row }) => {
                const balanceDue = row.original.amount_outstanding;

                if (typeof balanceDue !== "number") {
                    return <span className="font-semibold">--</span>;
                }

                return (
                    <span
                        className={cn("font-semibold", {
                            "text-danger": balanceDue > 0,
                        })}
                    >
                        {balanceDue > 0
                            ? `(${showMoneyInAppFormat(balanceDue)})`
                            : showMoneyInAppFormat(Math.abs(balanceDue))}
                    </span>
                );
            },
        },
        {
            accessorKey: "cpt_code",
            header: "CPT CODE",
            cell: ({ row }) => row.original.cpt_code || "--",
        },
    ];

    React.useEffect(() => {
        if (exportQuery.isSuccess) {
            setIsExporting(false);
        }
    }, [exportQuery.isLoading, exportQuery.isSuccess]);

    return (
        <>
            <Card classNames="mb-[--spacing-header] p-0">
                <div className="flex [border-bottom:1px_solid_var(--color-stroke)] p-24 justify-between items-center">
                    <p className="font-medium text-base">Set the details</p>
                    <Button
                        variant="secondary"
                        mode="filled"
                        type="button"
                        onClick={handleReset}
                    >
                        Reset
                    </Button>
                </div>
                <div className="p-24">
                    <form id="aging-coinsurance-preview">
                        <div className="fg">
                            <Select
                                ref={clientSelectRef}
                                isSearchable
                                filterOption={() => true}
                                isMulti
                                placeholder="Client name (Type multiple)"
                                value={
                                    showDefaultClients
                                        ? ClientNamesForSelect?.filter(
                                              (client) =>
                                                  searchParams
                                                      .getAll("clients[]")
                                                      .includes(client.value)
                                          ) || []
                                        : undefined
                                }
                                aria-label="Client name"
                                label="Client name"
                                options={filteredClients}
                                onChange={handleClientsChange}
                                onInputChange={(value) => {
                                    setShowClientOptions(true);
                                    setClientSearchValue(value);
                                    setShowDefaultClients(false);
                                }}
                                onBlur={() => {
                                    setShowClientOptions(false);
                                }}
                                defaultValue={
                                    !showDefaultClients
                                        ? ClientNamesForSelect?.filter(
                                              (client) =>
                                                  searchParams
                                                      .getAll("clients[]")
                                                      .includes(client.value)
                                          ) || []
                                        : undefined
                                }
                                openMenuOnClick={false}
                                menuIsOpen={clientSearchValue.length > 0}
                                noOptionsMessage={({ inputValue }) =>
                                    fetchingClients && inputValue
                                        ? "Fetching clients..."
                                        : "No options"
                                }
                            />
                        </div>

                        <div className="fg">
                            <Select
                                ref={providerSelectRef}
                                isSearchable
                                filterOption={() => true}
                                isMulti
                                placeholder="Provider name (Type multiple)"
                                value={
                                    showDefaultProviders
                                        ? ProviderNamesForSelect?.filter(
                                              (provider) =>
                                                  searchParams
                                                      .getAll("providers[]")
                                                      .includes(provider.value)
                                          ) || []
                                        : undefined
                                }
                                aria-label="Provider name"
                                label="Provider name"
                                options={filteredProviders}
                                onChange={handleProviderChange}
                                onInputChange={(value) => {
                                    setShowProviderOptions(true);
                                    setProviderSearchValue(value);
                                    setShowDefaultProviders(false);
                                }}
                                onBlur={() => {
                                    setShowProviderOptions(false);
                                }}
                                defaultValue={
                                    !showDefaultProviders
                                        ? ProviderNamesForSelect?.filter(
                                              (provider) =>
                                                  searchParams
                                                      .getAll("providers[]")
                                                      .includes(provider.value)
                                          ) || []
                                        : undefined
                                }
                                openMenuOnClick={false}
                                menuIsOpen={providerSearchValue.length > 0}
                            />
                        </div>

                        <div className="fg two fg-space-between flex">
                            <div>
                                <Select
                                    label="Date range"
                                    options={DurationTypesForSelect}
                                    placeholder="Select date range"
                                    onChange={handleCustomDateChange}
                                    value={
                                        DurationTypesForSelect.find(
                                            (option) => {
                                                if (customDateFilter)
                                                    return (
                                                        option.value ===
                                                        customDateFilter
                                                    );
                                                return undefined;
                                            }
                                        ) || null
                                    }
                                    isClearable={Boolean(customDateFilter)}
                                />
                            </div>
                            <div>
                                <p className="font-medium mb-1 !text-caption text-textSec">
                                    Date
                                </p>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <div className="flex justify-between h-8 border-[1.5px] border-[#E7E7E7] rounded-md [border:0.5px_solid_var(--color-stroke)] px-2 shadow-active items-center">
                                            <Button
                                                className="w-full text-xs justify-start font-normal text-gray data-[state=open]:border-primary "
                                                variant="normal"
                                                mode="outline"
                                                aria-label="request-changes"
                                                type="button"
                                            >
                                                {fromDateFilterToDate &&
                                                toDateFilterToDate
                                                    ? `${format(
                                                          fromDateFilterToDate,
                                                          "MM/dd/yyyy"
                                                      )} - ${format(
                                                          toDateFilterToDate,
                                                          "MM/dd/yyyy"
                                                      )}`
                                                    : "Select date"}
                                            </Button>
                                            <div className="pr-2">
                                                <CalendarIcon className="size-3.5" />
                                            </div>
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent align="start">
                                        <DatePicker
                                            label="From"
                                            placeholderText="MM/DD/YYYY"
                                            selected={fromDateFilterToDate}
                                            onChange={
                                                handleFromDateFilterChange
                                            }
                                            maxDate={
                                                toDateFilterToDate ||
                                                new Date(Date.now())
                                            }
                                            isClearable
                                        />
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>

                        <div className="fg fg-space-between two flex">
                            <div>
                                <p className="text-xs font-medium pb-[2px]">
                                    Day intervals
                                </p>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <div className="flex justify-between h-8 [border:0.5px_solid_var(--color-stroke)] shadow-active px-2 rounded-md items-center">
                                            <Button
                                                className="w-full text-xs justify-between items-center font-normal data-[state=open]:border-primary"
                                                variant="normal"
                                                mode="outline"
                                            >
                                                <span className="text-textSec">
                                                    Day intervals
                                                </span>

                                                <ChevronDownIcon stroke="#576a81" />
                                            </Button>
                                        </div>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {dayIntervalForSelect.map(
                                            (interval) => (
                                                <DropdownItem
                                                    key={interval}
                                                    isMulti
                                                    itemValue={interval}
                                                    values={getIntervals()}
                                                    onClick={() =>
                                                        handleDayIntervalChange(
                                                            interval
                                                        )
                                                    }
                                                >
                                                    {removeEnumUnderscore(
                                                        interval
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                            <div>
                                <p className="font-medium mb-1 !text-caption text-textSec">
                                    Bill status
                                </p>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <div className="flex justify-between h-8  [border:0.5px_solid_var(--color-stroke)] shadow-active px-2 rounded-md items-center">
                                            <Button
                                                className="w-full text-xs justify-between items-center font-normal data-[state=open]:border-primary"
                                                variant="normal"
                                                mode="outline"
                                            >
                                                <span className="text-textSec">
                                                    Bill status
                                                </span>

                                                <ChevronDownIcon stroke="#576a81" />
                                            </Button>
                                        </div>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        {Object.values(BillStatus).map(
                                            (status) => (
                                                <DropdownItem
                                                    key={status}
                                                    onClick={() =>
                                                        handleStatusChange(
                                                            status
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={status}
                                                    values={getInvoiceStatus()}
                                                >
                                                    {getBillStatusFilterLabel(
                                                        status
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                            </div>
                        </div>
                    </form>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "invoice_status[]",
                                values: getInvoiceStatus(),
                            },
                            {
                                key: "day_intervals[]",
                                values: getIntervals(),
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                    />
                </div>
            </Card>

            {isLoading && shouldFetchReport && <Loader />}
            {error && shouldFetchReport && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        "Cannot display breakdown at this time, please try again later"
                    }
                />
            )}

            {data && isSuccess && data.data.length === 0 && shouldFetchReport && (
                <div>
                    <ListState
                        stateHelperText="Billing breakdown list will be displayed here when available"
                        isError={false}
                        emptyMessage="No billing breakdown to display"
                    />
                </div>
            )}

            {data &&
                data?.data &&
                isSuccess &&
                data.data.length > 0 &&
                shouldFetchReport && (
                    <div className="flex flex-col gap-y-3">
                        <div>
                            <Button
                                variant="primary"
                                type="button"
                                onClick={handleExportClick}
                                disabled={isExporting}
                            >
                                Export
                            </Button>
                        </div>

                        <Table
                            data={data.data}
                            columns={columns}
                            hasPagination={data.total_count > 20}
                            setSortConfig={setSortConfig}
                            sortConfig={sortConfig}
                            sortableColumns={sortableColumns}
                            handleRowClick={handleRowClick}
                            pagination={
                                <Pagination
                                    totalCount={data.total_count}
                                    totalPages={data.total_pages}
                                    currentPage={pageFilter}
                                    onPageChange={handlePageChange}
                                    limit={limitFilter}
                                    onLimitChange={handleLimitChange}
                                />
                            }
                        />
                    </div>
                )}
        </>
    );
}
